import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { API_BASE_URL, API_ENDPOINTS } from '../constants';
import { storageService } from '../services';

interface WeightRecord {
  id: string;
  date: string;
  weight: number;
}

interface ADGDataPoint {
  date: string;
  adg: number;
  weight: number;
  cumulativeGain: number;
}

interface ADGChartProps {
  cowId: string;
  startDate?: Date;
  endDate?: Date;
}

const ADGChart: React.FC<ADGChartProps> = ({ cowId, startDate, endDate }) => {
  const [data, setData] = useState<ADGDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avgADG, setAvgADG] = useState<number>(0);

  useEffect(() => {
    fetchWeightData();
  }, [cowId, startDate, endDate]);

  const fetchWeightData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = storageService.getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.HEALTH.WEIGHT_BY_COW(cowId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch weight records');
      }

      const records: WeightRecord[] = await response.json();

      // Filter by date range if provided
      let filteredRecords = records;
      if (startDate && endDate) {
        filteredRecords = records.filter((record) => {
          const recordDate = new Date(record.date);
          return recordDate >= startDate && recordDate <= endDate;
        });
      }

      // Sort by date
      filteredRecords.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      if (filteredRecords.length === 0) {
        setData([]);
        setAvgADG(0);
        return;
      }

      // Calculate ADG for each record
      const adgData: ADGDataPoint[] = [];
      const firstWeight = filteredRecords[0].weight;
      const firstDate = new Date(filteredRecords[0].date);

      filteredRecords.forEach((record, index) => {
        if (index === 0) {
          adgData.push({
            date: record.date,
            adg: 0,
            weight: record.weight,
            cumulativeGain: 0,
          });
        } else {
          const currentDate = new Date(record.date);
          const daysDiff =
            (currentDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);
          const weightGain = record.weight - firstWeight;
          const adg = daysDiff > 0 ? weightGain / daysDiff : 0;

          adgData.push({
            date: record.date,
            adg: parseFloat(adg.toFixed(2)),
            weight: record.weight,
            cumulativeGain: parseFloat(weightGain.toFixed(2)),
          });
        }
      });

      // Calculate average ADG
      if (filteredRecords.length > 1) {
        const totalDays =
          (new Date(filteredRecords[filteredRecords.length - 1].date).getTime() -
            firstDate.getTime()) /
          (1000 * 60 * 60 * 24);
        const totalGain =
          filteredRecords[filteredRecords.length - 1].weight - firstWeight;
        const avgAdg = totalDays > 0 ? totalGain / totalDays : 0;
        setAvgADG(parseFloat(avgAdg.toFixed(2)));
      }

      setData(adgData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const formatXAxis = (tickItem: string) => {
    try {
      return format(new Date(tickItem), 'dd/MM/yy');
    } catch {
      return tickItem;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 p-3 rounded shadow-lg">
          <p className="font-semibold text-sm mb-2">
            {format(new Date(label), 'dd MMM yyyy')}
          </p>
          {payload.map((entry: any, index: number) => {
            const value =
              typeof entry.value === 'number' ? entry.value : Number(entry.value) || 0;
            let unit = '';
            if (entry.dataKey === 'adg') unit = ' กก./วัน';
            else if (entry.dataKey === 'weight') unit = ' กก.';
            else if (entry.dataKey === 'cumulativeGain') unit = ' กก.';

            return (
              <p key={index} className="text-xs" style={{ color: entry.color }}>
                {entry.name}: {value.toFixed(2)}
                {unit}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">ไม่มีข้อมูลน้ำหนักในช่วงเวลาที่เลือก</p>
      </div>
    );
  }

  return (
    <div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-700 mb-1">
            ADG เฉลี่ย (Average Daily Gain)
          </h3>
          <p className="text-2xl font-bold text-blue-900">
            {avgADG.toFixed(2)} <span className="text-lg">กก./วัน</span>
          </p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-green-700 mb-1">
            น้ำหนักเริ่มต้น
          </h3>
          <p className="text-2xl font-bold text-green-900">
            {data[0]?.weight.toFixed(2)} <span className="text-lg">กก.</span>
          </p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-purple-700 mb-1">
            น้ำหนักปัจจุบัน
          </h3>
          <p className="text-2xl font-bold text-purple-900">
            {data[data.length - 1]?.weight.toFixed(2)}{' '}
            <span className="text-lg">กก.</span>
          </p>
          <p className="text-xs text-purple-600 mt-1">
            เพิ่ม {data[data.length - 1]?.cumulativeGain.toFixed(2)} กก.
          </p>
        </div>
      </div>

      {/* Chart */}
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        กราฟ ADG และน้ำหนักตามเวลา
      </h3>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={formatXAxis} />
          <YAxis yAxisId="left" label={{ value: 'ADG (กก./วัน)', angle: -90, position: 'insideLeft' }} />
          <YAxis yAxisId="right" orientation="right" label={{ value: 'น้ำหนัก (กก.)', angle: 90, position: 'insideRight' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            yAxisId="left"
            type="monotone"
            dataKey="adg"
            stroke="#3b82f6"
            strokeWidth={2}
            name="ADG"
            dot={{ r: 4 }}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="weight"
            stroke="#10b981"
            strokeWidth={2}
            name="น้ำหนัก"
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ADGChart;
